/**
 * @fileoverview Summary of a Meta CSV import preview: range, totals by country, match split, unmatched products.
 * @module features/ad-spends/components/import/ImportPreviewSummary
 */

import { Fragment, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoney } from '@/shared/utils/money'
import type { MetaImportPreview } from '../../schemas/adSpendImportSchema'

export interface ImportPreviewSummaryProps {
  preview: MetaImportPreview
}

export function ImportPreviewSummary({ preview }: ImportPreviewSummaryProps): ReactElement {
  const { t, i18n } = useTranslation('features-ad-spends')
  const money = (value: number): string => formatMoney(value, preview.currency, i18n.language)

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium">
        {t('import.preview.heading', {
          defaultValue: '{{from}} → {{to}} · {{total}}',
          from: preview.dateFrom,
          to: preview.dateTo,
          total: money(preview.totalSpend),
        })}
      </p>

      {preview.existingDaysReplaced > 0 && (
        <p role="note" className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <AlertTriangle className="size-3.5 shrink-0" />
          {t('import.preview.replaces', {
            defaultValue: 'Replaces Meta spend already stored for {{days}} day(s) in this range.',
            days: preview.existingDaysReplaced,
          })}
        </p>
      )}

      <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
        {preview.byCountry.map(row => (
          <Fragment key={row.country}>
            <dt className="text-muted-foreground">{row.country}</dt>
            <dd className="text-right tabular-nums">{money(row.amount)}</dd>
          </Fragment>
        ))}
        <dt className="border-t border-border pt-1 text-muted-foreground">
          {t('import.preview.matched', { defaultValue: 'Matched to products' })}
        </dt>
        <dd className="border-t border-border pt-1 text-right tabular-nums">{money(preview.matchedAmount)}</dd>
        <dt className="text-muted-foreground">{t('import.preview.general', { defaultValue: 'Not tied to a product (general)' })}</dt>
        <dd className="text-right tabular-nums">{money(preview.generalAmount)}</dd>
        <dt className="text-muted-foreground">{t('import.preview.unmatched', { defaultValue: 'Unmatched (goes to Needs review)' })}</dt>
        <dd className="text-right tabular-nums">{money(preview.unmatchedAmount)}</dd>
      </dl>

      {preview.unmatched.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {t('import.preview.unmatchedTitle', {
              defaultValue: 'Unmatched products ({{n}}) — map them in Needs review after importing',
              n: preview.unmatched.length,
            })}
          </p>
          <div className="max-h-56 overflow-y-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('import.preview.table.productId', { defaultValue: 'Product ID' })}</TableHead>
                  <TableHead>{t('import.preview.table.product', { defaultValue: 'Product' })}</TableHead>
                  <TableHead className="text-right">{t('import.preview.table.amount', { defaultValue: 'Amount' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.unmatched.map(product => (
                  <TableRow key={product.productId}>
                    <TableCell className="font-mono text-xs">{product.productId}</TableCell>
                    <TableCell className="max-w-64 truncate">{product.title ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(product.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}
